import { Badge } from '@/components/atoms/Badge';
import { Icon } from '@/components/atoms/Icon';
import { Button } from '@/components/atoms/Button';
import type { BookingListItem } from '@/services/bookings';
import styles from './BookingCard.module.scss';

interface BookingCardProps {
  booking: BookingListItem;
  onCancel?: (id: number) => void;
  onLeave?: (id: number) => void;
  isMine: boolean;
  joinedAsStudent: boolean;
}

const ANT_CANCEL_HOURS = 6;

function formatTime(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function hoursUntil(iso: string): number {
  return (new Date(iso).getTime() - Date.now()) / (1000 * 60 * 60);
}

export function BookingCard({ booking, onCancel, onLeave, isMine, joinedAsStudent }: BookingCardProps) {
  const isGroup = booking.type === 'gr';
  const start = formatTime(booking.scheduled_at);
  const canCancel = isMine && hoursUntil(booking.scheduled_at) >= ANT_CANCEL_HOURS && booking.status === 'scheduled';
  const isCancelled = booking.status === 'cancelled';

  return (
    <article className={`${styles.card} ${isCancelled ? styles.cancelled : ''}`}>
      <div className={styles.left}>
        <div className={styles.timeBadge} aria-hidden="true">
          <Icon name="clock" size={14} />
          <span>{start}</span>
        </div>
        <span className={styles.duration}>{booking.duration_min} min</span>
      </div>

      <div className={styles.body}>
        <div className={styles.headRow}>
          <h4 className={styles.title}>
            {isGroup ? 'Clase grupal' : 'Clase individual'} con {booking.teacher_name}
          </h4>
          <Badge tone={isGroup ? 'warning' : 'accent'}>
            {isGroup ? `Grupo · ${booking.active_students}/${booking.max_students}` : 'Individual'}
          </Badge>
        </div>
        {booking.notes && <p className={styles.notes}>{booking.notes}</p>}
        {isCancelled && <Badge tone="danger">Cancelada</Badge>}
      </div>

      <div className={styles.actions}>
        {canCancel && !isGroup && onCancel && (
          <Button variant="ghost" size="sm" onClick={() => onCancel(booking.id)}>
            <Icon name="x" size={14} /> Cancelar
          </Button>
        )}
        {canCancel && isGroup && joinedAsStudent && onLeave && (
          <Button variant="ghost" size="sm" onClick={() => onLeave(booking.id)}>
            <Icon name="x" size={14} /> Salir
          </Button>
        )}
      </div>
    </article>
  );
}
